import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { selectInstruments } from '@app/core/store/capital-structure-slice-selectors';
import useInstrumentSort from '@app/shared/hooks/useInstrumentSort';
import { CalcMethod } from '@app/shared/models/contracts/enums/shared-enums';
import {
  getOpmFilteredWaterfallInstruments,
  getPwermFilteredWaterfallInstruments,
} from '../utils/instrumentFilters';
import { useShouldUseOpmResults } from './useShouldUseOpmResults';

export interface InstrumentOption {
  value: string;
  viewValue: string;
}

const ALL_INSTRUMENTS_OPTION = { viewValue: 'All Instruments', value: '' };

export const useInstrumentOptions = (includeAllInstrumentsOption = false) => {
  const { caseId } = useParams<{ caseId: string }>();
  const isOpmOnly = useAppSelector(
    (state) => state.project.projectDraft.details.calcMethod === CalcMethod.OPM
  );
  const useOpmResults = useShouldUseOpmResults();

  const projectInstruments = useAppSelector(selectInstruments);
  const opmWaterfallResults = useAppSelector(
    (state) => state.opmCalculation.riskFreeValues.waterfalls
  );
  const pwermWaterfallResults = useAppSelector(
    (state) => state.pwerm2Calculation.calculatedResults.waterfall
  );

  const filteredInstruments = useMemo(() => {
    if (useOpmResults) {
      return getOpmFilteredWaterfallInstruments(opmWaterfallResults, projectInstruments);
    } else {
      return getPwermFilteredWaterfallInstruments(
        pwermWaterfallResults,
        projectInstruments,
        caseId
      );
    }
  }, [useOpmResults, opmWaterfallResults, pwermWaterfallResults, projectInstruments, caseId]);

  const sortedInstruments = useInstrumentSort(filteredInstruments);

  const instrumentOptions: InstrumentOption[] = useMemo(() => {
    let options = sortedInstruments.map((instrument) => ({
      value: instrument.instrumentId,
      viewValue: instrument.instrumentNarrative,
    }));

    if (includeAllInstrumentsOption && options.length > 1) {
      options = [ALL_INSTRUMENTS_OPTION, ...options];
    }

    return options;
  }, [sortedInstruments, includeAllInstrumentsOption]);

  return {
    instrumentOptions,
    isOpmOnly,
  };
};
